<template>
  <v-card shaped v-if="internalEntries">
    <v-card-title>
      <h4 class="h4">{{getPeriodString}}</h4>
      <v-spacer></v-spacer>
      <span class="subtitle-1 primary--text" v-if="!validationTimeReached">
        {{getValidationString}}
      </span>
    </v-card-title>
    <v-card-text>
    <v-container>
      <v-row align="center">
        <v-col v-if="frequency === frequencyKeys.QUOTIDIEN"></v-col>
        <template v-for="(field, index) in fields">
          <v-col v-if="field.show || field.required" :key="'fieldHeader' + index">{{field.name}}</v-col>
        </template>
      </v-row>
      <v-divider horizontal class="my-3"></v-divider>
      <template v-for="(entry, index) in internalEntries">
        <EntryFormRow
          v-model="internalEntries[index]"
          :key="'row' + index"
          :fields="fields"
          :showDay="frequency === frequencyKeys.QUOTIDIEN"
          :validation="$v.internalEntries.$each[index]"
          @edited="edited = true"
        />
      </template>
      <template v-if="frequency === frequencyKeys.QUOTIDIEN">
        <v-divider class="my-3"></v-divider>
        <EntryFormTotalRow
          :entries="internalEntries"
          :fields="fields"
        />
      </template>
    </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
          color="primary"
          large
          @click="saveData"
          rounded
          outlined
          :disabled="hasBeenSent && !admin"
        >
          Enregistrer
        </v-btn>
        <v-btn
          v-if="canSend"
          color="success"
          outlined
          large
          @click="sendData"
          rounded
          :disabled="!validationTimeReached"
          :loading="sending"
        >
          <v-icon left>send</v-icon>
          Envoyer
        </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { countryKeys, frequencyKeys } from '@/config/fixedValues';
import { required, integer, decimal, minValue } from 'vuelidate/lib/validators'
import Entries from '@/services/entries'
import EntryFormRow from '@/components/Entry/EntryFormRow'
import EntryFormTotalRow from '@/components/Entry/EntryFormTotalRow'
import { endOfMonth, endOfWeek, isAfter, setDay, setHours, getWeek } from 'date-fns'
export default {
  name: 'EntryForm',
  components: { EntryFormRow, EntryFormTotalRow },
  props: {
    entries: {type: Array, required: true},
    frequency: {type: String, required: true}, // Input Frequency
    admin: {type: Boolean, required: false},
    user: {type: Object, required: true} // concerned user
  },
  data: () => ({
    frequencyKeys,
    internalEntries: [],
    edited: false,
    sending: false,
  }),
  watch: {
    entries: {
      immediate: true,
      handler: function (value) {
        this.internalEntries = value.map(entry => this.formatEntry(entry))
        this.edited = false
      }
    }
  },
  computed: {
    fields () {
      if (!this.entries.length) return []
      const turnoverType = this.entries[0].useTtc ? ' TTC ' : ' HT '
      return [
        { name: 'CA ' + turnoverType + this.entries[0].reducedRate + '%', key: 'turnoverReduced', required: this.entries[0].hasTurnoverReduced, type: 'ca', icon: this.currencyIcon },
        { name: 'CA ' + turnoverType + this.entries[0].intermediateRate + '%', key: 'turnoverIntermediate', required: this.entries[0].hasTurnoverIntermediate, type: 'ca', icon: this.currencyIcon },
        { name: 'CA ' + turnoverType + this.entries[0].normalRate + '%', key: 'turnoverNormal', required: this.entries[0].hasTurnoverNormal, type: 'ca', icon: this.currencyIcon },
        { name: 'CA N-1 ' + turnoverType, key: 'turnoverN1', required: this.entries[0].hasTurnoverN1, type: 'ca', icon: this.currencyIcon },
        { name: 'Budget ' + turnoverType, key: 'budget', show: this.entries[0].hasBudget && this.admin, type: 'ca', icon: this.currencyIcon },
        { name: 'Nombre d\'entrées', key: 'nbEntries', required: this.entries[0].hasNbEntries, type: 'number' },
        { name: 'Nombre d\'heures', key: 'nbHours', required: this.entries[0].hasNbHours, type: 'number' },
        { name: 'Transactions', key: 'nbTransactions', required: this.entries[0].hasNbTransactions, type: 'number' },
        { name: 'Quantité vendue', key: 'nbQuantity', required: this.entries[0].hasNbQuantity, type: 'number' },
        { name: 'Commentaire', key: 'comment', show: true },
      ].filter(field => field.required || field.show)
    },
    currencyIcon() {
      switch (this.user.country) {
        case countryKeys.SUISSE:
          return 'currency_franc';
        case countryKeys.FRANCE:
        default:
          return 'euro';
      }
    },

    firstEntryDate () {
      return this.entries[0].dateISO
    },

    hasBeenSent () {
      return this.entries.every(entry => entry.hasBeenSent)
    },

    canSend () {
      return !this.hasBeenSent && this.validationTimeReached
    },

    validationTime () {
      if (this.frequency === frequencyKeys.QUOTIDIEN || this.frequency === frequencyKeys.HEBDOMADAIRE) {
        return this.entries[0] &&
          setDay(
            setHours(this.firstEntryDate, this.user.mall.validationHour),
            this.user.mall.validationDay,
            {weekStartsOn: 1}
          )
      } else {
        return this.entries[0] && setHours(endOfMonth(this.firstEntryDate), this.user.mall.validationHour)
      }
    },

    validationTimeReached () {
      return this.validationTime && isAfter(new Date(), this.validationTime)
    },
    getPeriodString () {
      if (this.frequency === frequencyKeys.QUOTIDIEN || this.frequency === frequencyKeys.HEBDOMADAIRE) {
        return this.firstEntryDate && this.firstEntryDate.toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
            + ' au '
            + endOfWeek(this.firstEntryDate, {weekStartsOn: 1}).toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
            + ', Semaine '
            + getWeek(this.firstEntryDate, { weekStartsOn: 1, firstWeekContainsDate: 4 })
      } else {
        return this.firstEntryDate && this.firstEntryDate.toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
            + ' au '
            + endOfMonth(this.firstEntryDate).toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
      }
    },
    getValidationString () {
      if (this.frequency === frequencyKeys.QUOTIDIEN || this.frequency === frequencyKeys.HEBDOMADAIRE) {
        return this.validationTime && `Validation à partir de ${this.validationTime.toLocaleString('fr-FR', {weekday: 'long', hour: 'numeric'})}`
      } else {
        return this.validationTime && `Validation à partir du ${this.validationTime.toLocaleString('fr-FR', {day: 'numeric', month: 'long', hour: 'numeric'})}`
      }
    }
  },
  validations() {
    const internalEntries = {
      $each: {}
    }
    for (const field of this.fields) {
      internalEntries.$each[field.key] = {
        required: field.required ? required : false,
        integer: field.type === 'number' ? integer : false,
        decimal: field.type === 'ca' ? decimal : false,
        minValue: field.type === 'number' || field.type === 'ca' ? minValue(0) : false
      }
    }
    return {internalEntries}
  },
  methods: {
    validForm () {
      let isValid = true
      this.internalEntries.forEach((entry, index) => {
        if (entry.isUserOpen && this.$v.internalEntries.$each.$iter[index].$invalid) isValid = false
      })
      return isValid
    },
    async saveData () {
      if (true === this.sending) return
      this.$v.$touch()
      if (this.validForm()) {
        try {
          let requests = []
          for (const entry of this.internalEntries) {
            requests.push(Entries.updateEntry(entry))
          }
          await Promise.all(requests)
          this.edited = false
          this.$v.$reset()
          this.$emit('saved')
          this.$store.dispatch('successMessage', 'Les données ont bien été mises à jour')
        } catch (error) {
          this.$store.dispatch('errorMessage', 'Erreur survenue lors de la mise à jour des données')
          throw error
        }
      } else {
        this.$store.dispatch('errorMessage', 'Saisie incorrect, veuillez vérifier les champs en surbrillance.')
      }
    },

    async sendData () {
      if (true === this.sending) return
      this.$v.$touch()
      if (this.validForm()) {
        try {
          this.sending = true
          let requests = []
          for (const entry of this.internalEntries) {
            requests.push(Entries.updateEntry(entry))
          }
          await Promise.all(requests)
          this.edited = false
          this.sending = false
          await Entries.send(this.user.id)
          this.$v.$reset()
          this.$emit('sent')
          this.$store.dispatch('successMessage', 'Les données ont bien été envoyées')
        } catch (error) {
          this.sending = false
          this.$store.dispatch('errorMessage', 'Erreur survenue durant l\'envoie des données')
          throw error
        }
      } else {
        this.$store.dispatch('errorMessage', 'Saisie incorrect, veuillez vérifier les champs en surbrillance.')
      }
    },

    formatEntry (entry) {
      const entryFormatted = {...entry}
      for (const field of this.fields) {
        if (field.type === 'ca') {
          const ca = Number.parseFloat(entryFormatted[field.key])
          entryFormatted[field.key] = ca.toFixed(2)
        }
      }
      return entryFormatted
    }
  }
}
</script>
